/**
 * 卡片基础组件
 * 
 * @Author: Focci
 * @Date: 2023-07-17 20:18:36
 * @Last Modified by: Focci
 * @Last Modified time: 2023-07-17 20:18:36
 */

// import PropTypes from 'prop-types'
import Link from 'next/link'
import useApp from '@hook/useApp'
import clsx from 'clsx'
import AspectRatioCover from '@comp/AspectRatioCover'
import PropertyIcons from '@page/common/card/property/PropertyIcons'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import SVGLinked from '@img/linked.svg'
import Switch from '@comp/form/Switch'
import { isValid } from '@lib/utils'
import { useMemo } from 'react'
import { isFunction } from 'lodash-es'
import { Skeleton, Typography } from '@comp/material'
import { useLocale, useTranslations } from 'next-intl'
import { propertyDetail, schoolDetail, seasrchMap } from '@lib/route/house'
import { houseSearchType } from '@lib/config'
import EstimateTrackButton from './EstimateTrackButton'
import FavoriteButton from './FavoriteButton'

/**
 * 卡片 Scoff
 */
function CardScoff({
  showCover = true,
  coverProps = null,
  loading = false,
  url = '',
  children
}) {
  const { isMobile } = useApp()
  const width = isMobile ? 90 : 180
  const height = isMobile ? 80 : 160

  return (
    <div className="flex gap-2 md:gap-3 relative">
      {showCover && (
        <div className="w-20 md:w-36">
          {loading && <Skeleton variant="rectangular" height="100%" />}
          {!loading && (
            <Link href={url}>
              <AspectRatioCover 
                width={width} 
                height={height} 
                {...coverProps} 
              />
            </Link>
          )}
        </div>
      )}
      <div 
        className={clsx([
          'flex-auto w-48 md:w-52 flex flex-col mt-0 md:mt-2 p-2',
          !showCover && 'px-4'
        ])}
      >
        {children}
      </div>
    </div>
  )
}

/**
 * 卡片 Loading
 */
export function PopupCardSkeleton({
  showCover = true
}) {
  const { isMobile } = useApp()

  return (
    <CardScoff loading showCover={showCover}>
      <Typography variant="text" className="text-f.8 text-medium">
        <Skeleton style={{ width: '90%' }} />
      </Typography>
      <Typography variant="inherit" className="mt-2">
        <Skeleton />
      </Typography>
      <Typography variant="inherit">
        <Skeleton style={{ width: '60%' }} />
      </Typography>
      {!isMobile && (
        <Typography variant="inherit">
          <Skeleton style={{ width: '80%' }} />
        </Typography>
      )}
    </CardScoff>
  )
}

function SchoolCardMeta({ label, prefix = '' }) {
  if(!label) return null
  return (
    <span className="border border-cd8 rounded-sm px-0.5 text-f.6">
      {prefix}
      {label}
    </span>
  )
}

/**
 * 学校评分卡片
 */
function SchoolCardScore({ score, type, meta, passcheck }) {
  const isScorePass = score !== '0' && isValid(score)

  if(passcheck && !isScorePass) {
    return null
  }

  return (
    <div className="flex items-center bg-cf0 px-2 shrink-0 rounded-sm">
      <span>{type}</span>
      <span>{meta}</span>
      <span>:</span>
      <b className="text-warning text-f.8 whitespace-none pl-1">
        {!isScorePass ? '-' : score}
      </b>
    </div>
  )
}

function SchoolCardLink({ url, label, Tag = Link, className, ...props }) {
  return (
    <Tag 
      href={url}
      className={clsx(['text-primary flex items-center outline-none cursor-pointer', className])}
      {...props}
    >
      <span>{label}</span>
      <ArrowForwardIosIcon sx={{ fontSize: '.6rem' }} />
    </Tag>
  )
}

/**
 * 学校卡片
 */
export function SchoolCard({
  data = {},
  switchProps,
  events,
}) {
  const locale = useLocale()
  const ts = useTranslations('search')
  const tss = useTranslations('school')

  // 是否包含校网
  const hasSchoolZone = data.has_school_network > 0

  const isEQI = String(data.score_type).toLowerCase() === 'eqi'
  const searchSchoolProps = useMemo(() => {
    const { slug, id } = data
    const type = houseSearchType.residential

    return !isFunction(events?.onSchoolPanelSearch)
      ? { href: seasrchMap(type, `list_school-${slug}-${id}_page-1`, locale) }
      : { 
        Tag: 'div', 
        onClick: () => events.onSchoolPanelSearch({
          school: {
            label: data?.name,
            value: data?.id,
            slug: data?.slug,
          }
        })
      }
  }, [data, events, locale])

  if(!data) {
    return null
  }
  
  return (
    <div className="py-2 pl-2 pr-4 flex flex-col gap-[.4rem] min-w-56 max-w-72">
      <b className="text-f.8 font-bold line-clamp-1 max-w-72">{data.name}</b>
      <div className="flex flex-wrap gap-1">
        <SchoolCardMeta label={data.school_type} />
        <SchoolCardMeta label={data.year_range} prefix={`${ts('level')}:`} />
        <SchoolCardMeta label={data.gender} />
        <SchoolCardMeta label={data.authority} />
        <SchoolCardMeta label={hasSchoolZone ? tss('hasZone') : tss('noZone')} />
      </div>
      <div className="flex items-center gap-x-1">
        {data.score && data.score_type && (
          <SchoolCardScore 
            score={data.score} 
            type={data.score_type} 
            meta={(isEQI && data.score_year) ? `(${data.score_year})` : tss('score')} 
          />
        )}
        <SchoolCardScore 
          score={data.decile} 
          type="Decile" 
          meta={tss('score')} 
          passcheck 
        />
      </div>
      <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
        <SchoolCardLink label={tss('schoolDetail')} url={schoolDetail(`${data.slug}-${data.id}`, locale)} />
        <SchoolCardLink
          className="mr-auto"
          label={hasSchoolZone ? tss('propertiesWithinSchoolZone') : tss('nearbyProperties')}
          {...searchSchoolProps}
        />
        <div className="flex items-center gap-1">
          {hasSchoolZone ? tss('zone') : tss('nearbyPropertiesWithinFiveKilometers')}
          <Switch small {...switchProps} />
        </div>
      </div>
    </div>
  )
}

function CardChip({
  label = 'Sold',
  className = 'bg-error absolute left-2 top-2 px-1'
}) {
  return (
    <span 
      className={clsx([
        'text-white rounded-sm text-f.6 whitespace-nowrap',
        className
      ])}
    >
      {label}
    </span>
  )
}

function EstimateChip({ type }) {
  const tm = useTranslations('menu')
  const th = useTranslations('house')
  const isAvm = type === 'avm'

  return (
    <CardChip 
      className={clsx(['px-1', isAvm ? 'bg-success' : 'bg-meta'])}
      label={isAvm ? tm('estimate') : th('govcv')} 
    />
  )
}

/**
 * 房源卡片
 */
export function PropertyCard({
  type = '',
  data = {}
}) {
  const locale = useLocale()
  const th = useTranslations('house')
  const url = propertyDetail(data.slug, locale)
  const isSold = type === houseSearchType.sold
  const isEstimate = [
    houseSearchType.estimate,
    'stateHouse' // 政府公屋
  ].indexOf(type) > -1
  
  return (
    <CardScoff
      url={url}
      showCover={!isEstimate}
      coverProps={{
        subtile: true,
        src: data.cover,
        alt: data.name,
      }}
    >
      {isSold && <CardChip />}
      <Link href={url} className="flex-auto">
        <div className="flex items-center gap-1 text-f.7 md:text-f.8 text-medium">
          {isEstimate && <EstimateChip type={data.estimateType} />}
          <div className="line-clamp-1 md:line-clamp-2">{data.name}</div>
        </div>
        <div className="mt-0 md:mt-1 text-f.7 line-clamp-1">
          {data.street}
        </div>
        <div className="mt-0 md:mt-1">
          <PropertyIcons data={data} />
        </div>
      </Link>
      <div className="mt-2 hidden md:flex items-center gap-2 overflow-auto">
        {!isSold && !isEstimate && (
          <FavoriteButton house={data} houseId={data.id} />
        )}
        {(isEstimate || isSold) && (
          <EstimateTrackButton
            propertyId={data.id}
          />
        )}
        <Link href={url}>
          <div 
            className="
              border border-1 border-primary bg-primary/10 rounded-full px-3 py-1 
              flex items-center text-primary
            "
          >
            <SVGLinked width=".7rem" className="shrink-0" />
            <span className="ml-1 whitespace-nowrap">
              {th('details')}
            </span>
          </div>
        </Link>
      </div>
    </CardScoff>
  )
}
